import { InputHTMLAttributes, PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';
import { VariantProps } from 'tailwind-variants';
import { IsVisible } from '../../../../utils';
import { FieldBase, IFieldBaseProps } from '../../Base';
import { checkboxStyles } from '../styles';
import { CheckboxFieldIcon, ICheckboxFieldIconProps } from '../utils/Icon';

export interface ICheckboxFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'name' | 'type'>,
    VariantProps<typeof checkboxStyles.input> {
  containerProps?: {
    className?: string;
    rest?: Omit<IFieldBaseProps, 'className'>;
  };
  hideIcon?: boolean;
  iconProps?: ICheckboxFieldIconProps;
  label?: string;
  labelProps?: {
    bold?: boolean;
    className?: string;
    color?: string;
    italic?: boolean;
    shouldInjectAsHTML?: boolean;
    underline?: boolean;
  };
  name: string;
}

export function CheckboxField({
  children,
  className,
  containerProps,
  design,
  disabled = false,
  hideIcon = false,
  iconProps,
  inputSize = 'md',
  label,
  labelProps,
  name,
  onChange,
  readOnly = false,
  value,
  ...rest
}: PropsWithChildren<ICheckboxFieldProps>) {
  return (
    <FieldBase
      className={twMerge('px-0', containerProps?.className)}
      iconPosition="right"
      {...containerProps?.rest}
    >
      <label
        className={checkboxStyles.label({
          design,
          disabled: disabled || readOnly,
          errored: !!containerProps?.rest?.error,
          className: labelProps?.className,
          bold: labelProps?.bold,
          italic: labelProps?.italic,
          underline: labelProps?.underline,
        })}
        htmlFor={`${name}-${value}`}
        style={{
          color: labelProps?.color,
        }}
      >
        <input
          className={twMerge(
            checkboxStyles.input({
              design,
              errored: !!containerProps?.rest?.error,
              disabled: disabled || readOnly,
              inputSize,
              className,
            }),
          )}
          disabled={disabled}
          id={`${name}-${value}`}
          name={name}
          onChange={onChange}
          readOnly={readOnly}
          type="checkbox"
          value={value}
          {...rest}
        />

        {children}
        <IsVisible when={!!label}>
          <IsVisible when={!labelProps?.shouldInjectAsHTML}>
            <span className="flex-1">{label}</span>
          </IsVisible>
          <IsVisible when={!!labelProps?.shouldInjectAsHTML}>
            <span
              className="flex-1"
              dangerouslySetInnerHTML={{ __html: label as string }}
            />
          </IsVisible>
        </IsVisible>
        <IsVisible when={!hideIcon}>
          <CheckboxFieldIcon
            className={iconProps?.className}
            design={design}
            disabled={disabled || readOnly}
            rest={iconProps?.rest}
            size={inputSize}
          />
        </IsVisible>
      </label>
    </FieldBase>
  );
}
